<template>
  <div class="bzc-info bzc-prospects" :data-source="p.source" data-test="ask-bizminer-prospects">
    <div class="bzc-info-head">
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"></path></svg>
      <span>Prospect List</span>
    </div>
    <div class="bzc-info-body">
      <template v-if="p.source === 'count'">
        <div class="bzc-prospects-count" data-test="ask-bizminer-prospects-count">{{ countText }}</div>
        <p v-if="summary.length" class="bzc-prospects-summary" data-test="ask-bizminer-prospects-summary">{{ summary.join(' · ') }}</p>
        <a
          v-if="link"
          class="bzc-btn bzc-btn-primary"
          :href="link.href"
          :target="link.external ? '_blank' : null"
          :rel="link.external ? 'noopener noreferrer' : null"
          data-test="ask-bizminer-prospects-buy"
          @click="$emit('open', $event, link)"
        >Buy in the app</a>
      </template>

      <template v-else>
        <div v-if="p.combination.listName" class="bzc-confirm-product" data-test="ask-bizminer-prospects-list-name">{{ p.combination.listName }}</div>
        <p v-if="summary.length" class="bzc-prospects-summary">{{ summary.join(' · ') }}</p>
        <ul v-if="p.rows.length" class="bzc-prospects-rows" data-test="ask-bizminer-prospects-rows">
          <li v-for="(r, i) in p.rows" :key="r.bizid || i" class="bzc-prospects-row" data-test="ask-bizminer-prospects-row">
            <span class="bzc-prospects-name">{{ r.name || 'Company' }}</span>
            <span v-if="place(r)" class="bzc-prospects-meta">{{ place(r) }}</span>
            <span v-if="r.employees !== null || r.salesRange" class="bzc-prospects-meta">{{ facts(r) }}</span>
          </li>
        </ul>
        <p v-if="p.count > p.rows.length" class="bzc-fine" data-test="ask-bizminer-prospects-more">Showing {{ p.rows.length }} of {{ p.count.toLocaleString('en-US') }}.</p>
        <a
          v-if="link"
          class="bzc-btn bzc-btn-outline"
          :href="link.href"
          :target="link.external ? '_blank' : null"
          :rel="link.external ? 'noopener noreferrer' : null"
          data-test="ask-bizminer-prospects-open"
          @click="$emit('open', $event, link)"
        >Open in the app</a>
      </template>
    </div>
  </div>
</template>

<script>
// A Prospect List preview in an Ask Bizminer answer (PFD-235). Presentational: it shows the free count, or the
// rows of a list the account already owns, and links to the app. Nothing is bought here and no price is shown.
// Everything comes from chatCore.normalizeProspects (an allow-list) and is drawn as text, never as HTML.
import { prospectFilterLines, safeAppLink } from './chatCore';

export default {
  name: 'chat-prospects-card',
  props: {
    prospects: { type: Object, required: true },
  },
  computed: {
    p() {
      return this.prospects;
    },
    countText() {
      const n = this.p.count;
      return n.toLocaleString('en-US') + (n === 1 ? ' company matches' : ' companies match');
    },
    // Industry, area and the other filters, as plain lines.
    summary() {
      const c = this.p.combination;
      const out = [];
      if (c.industry) out.push(c.industry.name ? c.industry.name + ' (' + c.industry.code + ')' : 'Industry ' + c.industry.code);
      if (c.area) out.push(c.area.name || 'Area ' + c.area.id);
      if (c.zipCount) out.push(c.zipCount + (c.zipCount === 1 ? ' ZIP code' : ' ZIP codes'));
      return out.concat(prospectFilterLines(this.p.filters));
    },
    // The service's buyUrl, followed only when it points at this app or bizminer.com.
    link() {
      return safeAppLink(this.p.buyUrl, window.location.origin);
    },
  },
  methods: {
    place(r) {
      return [r.city, r.state].filter(Boolean).join(', ');
    },
    facts(r) {
      const out = [];
      if (r.employees !== null) out.push(r.employees.toLocaleString('en-US') + (r.employees === 1 ? ' employee' : ' employees'));
      if (r.salesRange) out.push(r.salesRange + ' sales');
      return out.join(' · ');
    },
  },
};
</script>

<style lang="scss">
.bzc-prospects-count { font-size: 18px; font-weight: 700; color: #003666; }
.bzc-prospects-summary { font-size: 13px; line-height: 1.45; color: #5b6575; overflow-wrap: anywhere; }
.bzc-prospects-rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; border: 1px solid #e3e8ee; border-radius: 8px; }
.bzc-prospects-row { display: flex; flex-wrap: wrap; gap: 2px 10px; padding: 8px 10px; border-top: 1px solid #e3e8ee; font-size: 13px; &:first-child { border-top: 0; } }
.bzc-prospects-name { flex: 1 1 100%; font-weight: 700; color: #22303f; overflow-wrap: anywhere; }
.bzc-prospects-meta { color: #5b6575; font-size: 12.5px; }
// Phones (PFD-237): the button spans the card, like the other chat cards.
@media (max-width: 600px) {
  .bzc-prospects .bzc-btn { align-self: stretch; }
  .bzc-prospects-row { padding: 10px; }
}
</style>
